"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

import { CopyButton } from "@/components/ui/copy-button";
import type { SitePromptBody } from "@/templates/site/types";

export default function PromptBodyCard({ body }: { body: SitePromptBody }) {
  const t = useTranslations("site.prompt");

  return (
    <section className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-medium text-foreground">{t("bodyTitle")}</h2>
        {body.unlocked ? null : (
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700">
            {t("afterPurchase")}
          </span>
        )}
      </div>

      {body.variables.length > 0 ? (
        <div className="mt-4 rounded-lg bg-muted p-4 text-sm leading-6 text-muted-foreground">
          <p>{body.unlocked ? t("fillYourself") : t("fillAfterPurchase")}</p>
          <p className="mt-2 flex flex-wrap gap-2">
            {body.variables.map((key) => (
              <span
                key={key}
                className="rounded-full bg-background px-3 py-1 font-mono text-xs text-foreground ring-1 ring-border"
              >
                {`{{${key}}}`}
              </span>
            ))}
          </p>
        </div>
      ) : null}

      {body.unlocked ? (
        <pre className="mt-4 max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted p-4 text-sm leading-6 text-foreground">
          {body.text || t("emptyBody")}
        </pre>
      ) : body.variables.length === 0 ? (
        <div className="mt-4 rounded-lg bg-muted p-4 text-sm leading-6 text-muted-foreground">
          <p>{t("copyAfterPurchase")}</p>
        </div>
      ) : null}

      {body.unlocked ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <CopyButton
            value={body.text}
            label={t("copy")}
            errorText={t("copyFailed")}
            className="rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            {(done) => (done ? t("copied") : t("copy"))}
          </CopyButton>
          <Link
            href="/member/ai/chat/"
            className="text-sm text-brand hover:underline"
          >
            {t("useInChat")}
          </Link>
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">{t("unlockHint")}</p>
      )}
    </section>
  );
}
